import type { SchedulerSummary } from '@/lib/api/generated/model';

/**
 * What a batch's scheduler is doing, in the words a person reads (service spec
 * 183 FR-012, UI spec 239 US1).
 *
 * `busy`: a decision is in flight. `warning`: the last decision started nothing
 * (or failed), nothing runs and tasks wait, so only a person can move the batch
 * on. A scheduler at rest has no line: there is nothing to act on.
 */
export interface SchedulerLine {
  tone: 'busy' | 'warning';
  title: string;
  decidedAt: string | null;
  /** The way forward, said only when the batch is stuck. */
  hint: string | null;
}

/**
 * `null` when there is nothing to say: a "Wait for a person" step, a batch with
 * no scheduler, a scheduler at rest, or a summary from a service that predates it.
 */
export function schedulerLine(
  scheduler: SchedulerSummary | null | undefined,
): SchedulerLine | null {
  if (!scheduler) return null;
  const decidedAt = scheduler.last_decided_at ?? null;
  switch (scheduler.state) {
    case 'picking':
      // The last decision is not the one in flight: say only that it is choosing.
      return {
        tone: 'busy',
        title: 'Choosing what to start…',
        decidedAt: null,
        hint: null,
      };
    case 'stalled':
      return {
        tone: 'warning',
        title: 'Nothing is running and tasks are waiting',
        decidedAt,
        hint: 'Start a task to move it on.',
      };
    case 'idle':
      // Resting, or waiting on a task that runs: nothing to show or to do. Each
      // decision and its reason is in the run's activity feed and on the parent's
      // comments.
      return null;
  }
}
